import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import {
  consoleLegalEntitiesUrl,
  redirectToPlatformSignIn,
} from "@/lib/platform-signin";
import { getAccountingHost } from "@/lib/api";
import { useSession } from "@/lib/session";
import { useOrganization } from "@/lib/organization";

function LoadingScreen() {
  return (
    <div className="grid min-h-screen place-items-center text-sm text-[var(--muted-foreground)]">
      Loading...
    </div>
  );
}

/**
 * Accounting is explicitly enabled for an organization during setup. A
 * validated organization without a host goes to onboarding instead of
 * rendering pages whose first reads would 404.
 */
export function RequireAccountingSetup({ children }: { children: ReactNode }) {
  const { scope } = useOrganization();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!scope) return;
    let cancelled = false;
    setStatus("loading");
    getAccountingHost()
      .then((host) => {
        if (cancelled) return;
        if (!host) {
          void navigate({ to: "/onboarding", replace: true });
          return;
        }
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [attempt, navigate, scope?.organization_id]);

  if (status === "ready") return <>{children}</>;
  if (status === "error") {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center text-sm text-[var(--muted-foreground)]">
        <div className="grid max-w-md justify-items-center gap-4">
          <p role="alert" className="text-[var(--danger)]">
            Accounting setup could not be checked.
          </p>
          <Button type="button" onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  return <LoadingScreen />;
}

/** Renders `children` once signed in; otherwise redirects to platform sign-in. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useSession();

  useEffect(() => {
    if (state.status === "anonymous") redirectToPlatformSignIn();
  }, [state.status]);

  if (state.status !== "authenticated") return <LoadingScreen />;
  return <>{children}</>;
}

/**
 * Renders `children` once Accounting has validated the organization supplied
 * by SYLVIQ Console. Organization selection belongs to Console; this guard
 * never recreates a choice from browser storage. The legal entity open inside
 * that organization is chosen by the app header.
 */
export function RequireOrganization({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const { scope, handoffOrganizationId, loading, error, refresh } =
    useOrganization();

  if (state.status !== "authenticated") return <LoadingScreen />;
  if (loading) return <LoadingScreen />;
  if (!scope) {
    if (handoffOrganizationId) {
      const entitiesUrl = consoleLegalEntitiesUrl(handoffOrganizationId);
      return (
        <div className="grid min-h-screen place-items-center px-6 text-center text-sm text-[var(--muted-foreground)]">
          <div className="grid max-w-md justify-items-center gap-4">
            {error ? (
              <>
                <p role="alert" className="text-[var(--danger)]">
                  Accounting could not load this organization&apos;s legal
                  entities.
                </p>
                <Button onClick={() => void refresh()} type="button">
                  Try again
                </Button>
              </>
            ) : (
              <>
                <p>
                  This organization has no legal entity yet. Add the person or
                  company whose books Accounting keeps, then reopen Accounting.
                </p>
                {entitiesUrl ? (
                  <Button asChild>
                    <a href={entitiesUrl}>Add a legal entity</a>
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center text-sm text-[var(--muted-foreground)]">
        Open Accounting from SYLVIQ Console after choosing an organization.
      </div>
    );
  }
  return <>{children}</>;
}
